import { useState } from "react";
import {
  Box,
  Container,
  IconButton,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Pencil } from "lucide-react";
import type { DeceasedPerson } from "../../../../../../../bookings/types/edit-booking";
import StreamViewer from "../../../shared-ui/StreamViewer";
import MemoriesTab from "../../../tabs/MemoriesTab";
import type { ICommentType } from "../../../tabs/MemoriesTab";
import ObituaryTab from "../../../tabs/OrbituaryTab";
import CommentCard from "../../../shared-ui/CommentCard";
import type {
  IStreamDetails,
  IThemeConfig,
} from "../../../types/memorial-types";
import { tokens } from "../../../../../../../../theme";
import BrandedHeader from "./components/BrandedHeader";
import ShareRail from "./components/ShareRail";
import RegisterBanner from "./components/RegisterBanner";

export interface FuneralHomeTemplateProps {
  streams: IStreamDetails[];
  activeStreamIndex: number;
  currentStream: IStreamDetails;
  onStreamChange: (index: number) => void;
  memorialTheme: IThemeConfig;
  deceasedData: DeceasedPerson | null;
  isLoadingDeceased: boolean;
  messages: ICommentType[];
  isAdmin: boolean;
  socketRef: React.RefObject<WebSocket | null>;
  handleApprove: (id: string, approve: boolean) => void;
  handleDelete: (id: string) => void;
  onMessageSent: () => void;

  currentPage: number;
  totalCount: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;

  funeralHomeName: string;
  funeralHomeLogo?: string | null;
  funeralHomeColor?: string;
  funeralHomeWebsite?: string | null;
  onBannerToggled?: () => void;
}

const DEFAULT_FUNERAL_HOME_COLOR = "#614338";

export default function FuneralHomeTemplate({
  streams,
  activeStreamIndex,
  currentStream,
  onStreamChange,
  memorialTheme,
  deceasedData,
  isLoadingDeceased,
  messages,
  isAdmin,
  socketRef,
  handleApprove,
  handleDelete,
  onMessageSent,
  currentPage,
  totalCount,
  pageSize,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  funeralHomeName,
  funeralHomeLogo,
  funeralHomeColor,
  funeralHomeWebsite,
  onBannerToggled,
}: FuneralHomeTemplateProps) {
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const brandColor = funeralHomeColor || DEFAULT_FUNERAL_HOME_COLOR;
  const bannerEnabled = currentStream.funeral_home_banner_enabled !== false;
  const [isEditingObituary, setIsEditingObituary] = useState(false);
  const hasObituaryText = Boolean(
    deceasedData?.orbituary &&
      deceasedData.orbituary.replace(/<[^>]*>/g, "").trim(),
  );
  const showEditPencil = isAdmin && hasObituaryText && !isEditingObituary;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fff" }}>
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "1200px",
          width: "100%",
          mx: "auto",
          pb: 4,
        }}
      >
        <BrandedHeader
          stream={currentStream}
          deceasedData={deceasedData}
          funeralHomeName={funeralHomeName}
          funeralHomeLogo={funeralHomeLogo}
          funeralHomeColor={brandColor}
        />
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          sx={{ mt: 2 }}
        >
          {!isMobile && (
            <ShareRail
              websiteStreamLink={currentStream.website_stream_link}
              title={currentStream.title}
              accentColor={brandColor}
            />
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StreamViewer
              streams={streams}
              activeStream={activeStreamIndex}
              onStreamChange={onStreamChange}
              memorialTheme={memorialTheme}
              currentStream={currentStream}
              deceasedName={deceasedData?.first_name}
              hideTabs={streams.length <= 1}
            />
          </Box>
        </Stack>

        <RegisterBanner
          funeralHomeName={funeralHomeName}
          funeralHomeColor={brandColor}
          websiteUrl={funeralHomeWebsite}
          isAdmin={isAdmin}
          streamId={currentStream.id}
          enabled={bannerEnabled}
          onToggled={onBannerToggled}
        />

        <Box
          sx={{
            mt: 3,
            borderRadius: "20px",
            border: "1px solid",
            borderColor: isMobile ? alpha(brandColor, 0.45) : "divider",
            ...(isMobile && { borderWidth: 2 }),
            p: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 4, md: 5 },
            }}
          >
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6" fontWeight={700}>
                  Obituary
                </Typography>
                {showEditPencil && (
                  <IconButton
                    size="small"
                    onClick={() => setIsEditingObituary(true)}
                    sx={{
                      bgcolor: alpha(brandColor, 0.12),
                      color: brandColor,
                      "&:hover": { bgcolor: alpha(brandColor, 0.22) },
                    }}
                  >
                    <Pencil size={14} />
                  </IconButton>
                )}
              </Stack>
              <ObituaryTab
                deceasedData={deceasedData}
                streamData={currentStream}
                isLoadingDeceased={isLoadingDeceased}
                isAdmin={isAdmin}
                accentColor={brandColor}
                variant="embedded"
                isEditing={isEditingObituary}
                onEditingChange={setIsEditingObituary}
              />
            </Box>

            <Box
              sx={{
                borderTop: { xs: "1px solid", md: "none" },
                borderLeft: { xs: "none", md: "1px solid" },
                borderColor: "divider",
                pt: { xs: 4, md: 0 },
                pl: { xs: 0, md: 5 },
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Memories
              </Typography>
              <MemoriesTab
                messages={messages}
                isAdmin={isAdmin}
                socketRef={socketRef}
                isMobile={isMobile}
                handleApprove={handleApprove}
                handleDelete={handleDelete}
                CommentCard={CommentCard}
                colors={colors}
                currentPage={currentPage}
                totalCount={totalCount}
                pageSize={pageSize}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
                onNextPage={onNextPage}
                onPreviousPage={onPreviousPage}
                onMessageSent={onMessageSent}
                variant="embedded"
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
